import type { LucideIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import { formatCrewStat } from "@/lib/format-crew-stat"
import { cn } from "@/lib/utils"

/**
 * OKS-474 — §18 social-proof stat tile (UX Spec §2.2).
 *
 * A white card with a Lucide icon, a big tabular-nums value
 * (`formatCrewStat` — full grouping below 1M, `M` suffix above), and a
 * label below. The accent variant (coins tile) tints the icon.
 *
 * # Accessibility (UX Spec §6, OKS-489)
 *
 * The card is exposed as a single `img`-role node carrying an `aria-label`
 * reading "{value} {label}" (e.g. "1,284 creators"), so a screenreader
 * announces the stat as one phrase. The `role="img"` is required: on the
 * default generic `<div>` role, ARIA 1.2 ignores `aria-label`, leaving the
 * tile silent (OKS-489). The icon and both text spans are `aria-hidden` so
 * the node announces exactly once — no fragmentation, no double-announce.
 */
interface CrewStatTileProps {
  value: number
  /** Already-translated label text (e.g. "creators", "coins given"). */
  label: string
  icon: LucideIcon
  /** Tints the icon with the accent-primary colour (the coins tile). */
  accent?: boolean
  testId?: string
}

export function CrewStatTile({
  value,
  label,
  icon: Icon,
  accent = false,
  testId,
}: CrewStatTileProps) {
  const { i18n } = useTranslation()
  const formatted = formatCrewStat(value, i18n.language)

  return (
    <Card
      role="img"
      data-testid={testId}
      aria-label={`${formatted} ${label}`}
      className="flex-1"
    >
      <CardContent className="flex flex-col gap-1 px-4 py-3">
        <Icon
          aria-hidden="true"
          className={cn(
            "size-5",
            accent ? "text-primary" : "text-muted-foreground",
          )}
        />
        <span className="text-2xl font-bold tabular-nums" aria-hidden="true">
          {formatted}
        </span>
        <span className="text-muted-foreground text-xs" aria-hidden="true">
          {label}
        </span>
      </CardContent>
    </Card>
  )
}
